import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { spacing } from "src/design-system/tokens/spacing";
import { font_size } from "src/design-system/tokens/typography";
import styled, { css } from "styled-components";
import color from "src/design-system/tokens/color";
import { useFocusState } from "../../FocusContext";
import type { MergeElementProps } from "../../utils/MergeElementProps";
import { AutocompleteIcon } from "./AutocompleteIcon";

type Props = {
  results: AutocompletePlace[];
  onSelectPlace: (place: AutocompletePlace) => void;
  focusRef?: React.RefObject<HTMLLIElement | null>;
  size?: "sm" | "md";
  iconClassName?: string;
  isInputFocused: boolean;
  prependItem?: React.ReactNode;
};

export function AutocompleteList({
  results,
  isInputFocused,
  prependItem,
  ...other
}: Props) {
  return (
    <ol>
      {isInputFocused && prependItem ? prependItem : null}
      {isInputFocused &&
        results.map((result, index) => {
          return (
            <AutocompleteItem
              key={result.canonicalName}
              place={result}
              index={index}
              {...other}
            />
          );
        })}
    </ol>
  );
}

type AutocompleteItemProps = MergeElementProps<
  "li",
  {
    index: number;
    place: AutocompletePlace;
    onSelectPlace: (place: AutocompletePlace) => void;
    focusRef?: React.RefObject<HTMLLIElement | null>;
    size?: "sm" | "md";
    iconClassName?: string;
  }
>;

function AutocompleteItem({
  index,
  place,
  onSelectPlace,
  size,
  iconClassName,
  focusRef,
  ...elementProps
}: AutocompleteItemProps) {
  const { focusedElement } = useFocusState();

  // We do index + 1 because 0 is the SearchInput, so, the 0th index of the
  // autocomplete list is at focus index 1.
  const isFocused = focusedElement.index === index + 1;

  return (
    <StyledListItem
      onClick={() => onSelectPlace(place)}
      isFocused={isFocused}
      data-testid={isFocused && "focused-autocomplete-item"}
      size={size}
      ref={isFocused ? focusRef : undefined}
      aria-label={place.longName}
      aria-selected={isFocused}
      role="option"
      {...elementProps}
    >
      <AutocompleteIcon className={iconClassName} kind={place.kind} />
      <ResultTitle size={size}>{place.longName}</ResultTitle>
    </StyledListItem>
  );
}

type ItemProps = {
  size?: "sm" | "md";
  isFocused: boolean;
};

const StyledListItem = styled.li<ItemProps>`
  display: flex;
  align-items: center;
  align-content: flex-start;
  text-align: left;
  padding: ${spacing.lg} ${spacing.md};

  ${(props) =>
    props.size === "sm" &&
    css`
      font-size: ${font_size.text_sm};
    `}

  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  // Fake a focus effect rather than allowing actual focus.
  background-color: ${(props) =>
    props.isFocused && color.bg.surface.primaryHover};

  &:hover {
    background-color: ${color.bg.surface.primaryHover};

    @media (hover: none) {
      background-color: ${(props) =>
        props.isFocused && color.bg.surface.primaryHover};
    }
  }

  &:active {
    background-color: ${color.bg.surface.primaryHover};
  }
`;

const ResultTitle = styled.span<{ size?: "sm" | "md" }>`
  margin-left: ${spacing.lg};
  color: ${color.text.primary};
  white-space: break-spaces;
  ${(props) =>
    props.size === "sm" &&
    css`
      margin-left: ${spacing.md};
    `}
`;
